import { z } from "zod";
import { zAnyNonNullish, zGuard, zKeyedRecord } from "./zod/utils.js";

/**
 * Helper to create a runtype that accepts T, null, or undefined.
 * Use this instead of `T.or(z.null())` when you don't want to enforce
 * the distinction between null and undefined.
 */
export const Nullish = <T extends z.ZodTypeAny>(type: T) =>
  type.or(z.null()).or(z.undefined());

import { ChartSpec } from "./chart/types.js";
import {
  RelativeDateOperationLiteral,
  RelativeDateUnitLiteral,
} from "./datetimeType.js";
import {
  BinaryColumnPredicateOpLiteral,
  ListBinaryColumnPredicateOpLiteral,
  UnaryColumnPredicateOpLiteral,
} from "./display-table/columnPredicateTypes.js";
import { DisplayTableOutput } from "./display-table/outputTypes.js";
import { CellType, MagicTypeAheadProviderLiteral } from "./enums.js";
import { assertNever, guardNever } from "./errors.js";
import { HqlAggregationFunctionLiteral } from "./hql/types.js";
import {
  DataConnectionId,
  DataSourceDatabaseId,
  DataSourceSchemaId,
  DataSourceTableId,
  MagicApiKeyId,
  MagicEventId,
} from "./idTypeBrands.js";
import {
  AnthropicModelLiteral,
  AnthropicModelParams,
  GeminiEAPMultiRegionLiteral,
  GeminiEAPRegionLiteral,
} from "./magic/anthropicTypes.js";
import {
  ChatCompletionsGateway,
  ChatCompletionsModelParams,
} from "./magic/chatCompletionsTypes.js";
import { Model } from "./magic/Model.js";
import { OpenAiModelParams } from "./magic/openAiTypes.js";
import { getNormalEnum } from "./pseudoEnums.js";
import { VariableName } from "./typeBrands.js";
import { isRecord } from "./utils/typeUtils.js";

// The date that Magic Terms of Service became part of the Hex Service Agreement
// All orgs created after this date have auto accepted the Magic TOS since its part of the default Hex Service Agreement
export const MAGIC_TERMS_PART_OF_SERVICE_AGREEMENT_DATE = new Date(
  "2025-02-26T00:00:00.000Z",
);
export function wasOrgCreatedAfterMagicTermsPartOfServiceAgreementDate(
  orgCreatedDateString: Date | string,
): boolean {
  return (
    new Date(orgCreatedDateString) > MAGIC_TERMS_PART_OF_SERVICE_AGREEMENT_DATE
  );
}

export const MagicSupportedImageMimeTypes = z.enum(["image/png", "image/jpeg"]);
export type MagicSupportedImageMimeTypes = z.infer<
  typeof MagicSupportedImageMimeTypes
>;

export const MagicSupportedDocumentMimeTypes = z.enum(["application/pdf"]);
export type MagicSupportedDocumentMimeTypes = z.infer<
  typeof MagicSupportedDocumentMimeTypes
>;

export const MAGIC_SUPPORTED_IMAGE_ACCEPT: Record<
  MagicSupportedImageMimeTypes,
  string[]
> = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
};

export const MagicSupportedTextMimeTypes = z.enum([
  "text/plain",
  "text/markdown",
  "text/csv",
  "text/tab-separated-values",
  "text/html",
  "text/css",
  "text/xml",
  "text/javascript",
  "text/yaml",
  "application/json",
  "application/xml",
  "application/javascript",
  "application/x-python",
  "application/x-yaml",
  "application/yaml",
  "application/x-sh",
]);
export type MagicSupportedTextMimeTypes = z.infer<
  typeof MagicSupportedTextMimeTypes
>;

export const MAGIC_SUPPORTED_TEXT_ACCEPT: Record<
  MagicSupportedTextMimeTypes,
  string[]
> = {
  "text/plain": [".txt"],
  "text/markdown": [".md"],
  "text/csv": [".csv"],
  "text/tab-separated-values": [".tsv"],
  "text/html": [".html", ".htm"],
  "text/css": [".css"],
  "text/xml": [".xml"],
  "text/javascript": [".js"],
  "text/yaml": [".yaml", ".yml"],
  "application/json": [".json"],
  "application/xml": [".xml"],
  "application/javascript": [".js"],
  "application/x-python": [".py"],
  "application/x-yaml": [".yaml", ".yml"],
  "application/yaml": [".yaml", ".yml"],
  "application/x-sh": [".sh"],
};

/**
 * Maximum file size for text files that can be read by agents (5MB).
 * This is a memory safeguard during file loading. The actual output to the agent
 * is bounded by line limits and truncation to avoid bloating the context.
 */
export const MAGIC_MAX_TEXT_FILE_SIZE = 5 * 1024 * 1024;

export const MAGIC_SUPPORTED_FILE_EXTENSIONS: ReadonlySet<string> = new Set(
  [
    ...Object.values(MAGIC_SUPPORTED_IMAGE_ACCEPT).flat(),
    ...Object.values(MAGIC_SUPPORTED_TEXT_ACCEPT).flat(),
  ].map((ext) => ext.toLowerCase()),
);

/** Every mime type an agent can read out of an uploaded file. */
export const MAGIC_SUPPORTED_FILE_MIMETYPES: readonly string[] = [
  ...Object.keys(MAGIC_SUPPORTED_IMAGE_ACCEPT),
  ...Object.keys(MAGIC_SUPPORTED_TEXT_ACCEPT),
];
export const SupportedExploreIntentLiteral = z.enum(["SUPPORTED_EXPLORATION"]);
export const UnsupportedIntentLiteral = z.enum([
  "UNSUPPORTED_EXPLORATION",
  "QUESTION",
  "VISUAL_DETAIL_EDIT",
  "UNSUPPORTED_VISUALIZATION",
]);
export const ExploreIntentLiteral = z.enum([
  ...SupportedExploreIntentLiteral.options,
  ...UnsupportedIntentLiteral.options,
]);
export type ExploreIntent = z.infer<typeof ExploreIntentLiteral>;
export const ExploreIntent = getNormalEnum(ExploreIntentLiteral);

export const EXPLORE_INTENT_MESSAGE_MAP: Record<ExploreIntent, string | null> =
  {
    [ExploreIntent.SUPPORTED_EXPLORATION]: null,
    [ExploreIntent.VISUAL_DETAIL_EDIT]:
      "Sorry, we can't change the chart styling with AI yet.",
    [ExploreIntent.UNSUPPORTED_EXPLORATION]:
      "The data required to answer this question is unavailable or unsupported.",
    [ExploreIntent.QUESTION]:
      "Sorry, we can't answer general questions with AI yet.",
    [ExploreIntent.UNSUPPORTED_VISUALIZATION]:
      "That visualization type isn't supported yet.",
  };

export const PromptTemplateTypeLiteral = z.enum([
  "APPEND_MARKDOWN",
  "APPEND_PYTHON",
  "APPEND_R",
  "DOCUMENT_PYTHON",
  "DOCUMENT_R",
  "DOCUMENT_SQL",
  "EDIT_MARKDOWN",
  "EDIT_PYTHON_DIFF",
  "EDIT_PYTHON_ADIFF",
  "EDIT_R",
  "EDIT_SQL_DIFF",
  "EDIT_SQL_ADIFF",
  "GENERATE_EXPLORE",
  "FIX_MARKDOWN",
  "FIX_PYTHON_BASIC",
  "FIX_PYTHON_DIFF",
  "FIX_PYTHON_ADIFF",
  "FIX_R",
  "FIX_SQL_DIFF",
  "FIX_SQL_ADIFF",
  "FIX_SQL_BASIC",
  "GENERATE_MARKDOWN",
  "GENERATE_PYTHON",
  "GENERATE_R",
  "GENERATE_SQL",
  "NAME_SQL_CELL",
  "NAME_PYTHON_CELL",
  "NAME_R_CELL",
  "NAME_MARKDOWN_CELL",
  "NAME_SQL_CELL_OUTPUT",
  "NAME_CHART_CELL_OUTPUT",
  "GENERATE_PROJECT_DESCRIPTION",
  "GENERATE_PROJECT_HYDE",
  "NAME_PROJECT",
  "NAME_PROJECT_FROM_PROMPT",
  "SIMPLE_CHART",
  "EDIT_CHART",
  "NAME_CHART_CELL",
  "SUMMARIZE_SCHEDULED_RUN",
]);

/**
 * @deprecated Use PromptTemplateV2 instead, with the modelFeatureKey system
 * rather than the old primaryAgentConfigOverride system. If in doubt, ask
 * @johnwiseheart for help.
 */
export type PromptTemplateType = z.infer<typeof PromptTemplateTypeLiteral>;
export const PromptTemplateType = getNormalEnum(PromptTemplateTypeLiteral);

export const GenerateExploreTemplateLiteral = z.enum(["GENERATE_EXPLORE"]);
export type GenerateExploreTemplate = z.infer<
  typeof GenerateExploreTemplateLiteral
>;
export const GenerateExploreTemplate = getNormalEnum(
  GenerateExploreTemplateLiteral,
);

export const MentionedDataframeName = z
  .string()
  .brand("MentionedDataframeName");
export type MentionedDataframeName = z.infer<typeof MentionedDataframeName>;

export const zMentionedDataframeName = z
  .string()
  .transform((val) => val as MentionedDataframeName);

export const MagicCellPayload = ChartSpec;
export type MagicCellPayload = z.infer<typeof MagicCellPayload>;

export const MagicRequestModeLiteral = z.enum([
  "GENERATE",
  "EDIT",
  "MORE_CELLS",
  "WRITEBACK",
  "MARKDOWN",
  "INPUT",
  "DATA_READ",
  "PREV_CELL_CODE",
  "QUESTION",
]);
export type MagicRequestMode = z.infer<typeof MagicRequestModeLiteral>;
export const MagicRequestMode = getNormalEnum(MagicRequestModeLiteral);

export const SingleCellMagicEditTypeLiteral = z.union([
  z.literal(CellType.CODE),
  z.literal(CellType.MARKDOWN),
  z.literal(CellType.SQL),
]);

export const SingleCellMagicEditType = getNormalEnum(
  SingleCellMagicEditTypeLiteral,
);
export type SingleCellMagicEditType = z.infer<
  typeof SingleCellMagicEditTypeLiteral
>;

// Magic cell types that only Hex knows about
export const HexMagicCellTypeLiteral = z.union([
  z.literal(CellType.TEXT),
  z.literal(CellType.METRIC),
  z.literal(CellType.EXPLORE),
]);
export const HexMagicCellType = getNormalEnum(HexMagicCellTypeLiteral);
export type HexMagicCellType = z.infer<typeof HexMagicCellTypeLiteral>;

export const MagicCellTypeLiteral = z.union([
  z.literal(CellType.CODE),
  z.literal(CellType.MARKDOWN),
  z.literal(CellType.SQL),
  z.literal(CellType.CHART),
  z.literal(CellType.EXPLORE),

  ...HexMagicCellTypeLiteral.options,
]);
export const MagicCellType = getNormalEnum(MagicCellTypeLiteral);
export type MagicCellType = z.infer<typeof MagicCellTypeLiteral>;

export const MagicNonStreamingCellType = z.literal(MagicCellType.CHART);
export type MagicNonStreamingCellType = z.infer<
  typeof MagicNonStreamingCellType
>;
export const MagicStreamingCellType = z.union([
  z.literal(MagicCellType.MARKDOWN),
  z.literal(MagicCellType.CODE),
  z.literal(MagicCellType.SQL),
]);
export type MagicStreamingCellType = z.infer<typeof MagicStreamingCellType>;

export const MagicEventSourceLiteral = z.enum([
  // [PROJECT LOGIC]
  // Any magic action originally from within an individual cell.
  "CELL",
  // Any magic action originating from within a project, but not an individual cell
  "PROJECT",
  // Any magic action originating from the "Add Cells" prompt bar in project logic.
  "ADD_CELLS",
  // Any magic action originating from an empty project's zero state bar.
  "EMPTY_PROJECT",
  // [EXPLORE]
  // Any magic action originating from within the Explore From Scratch editor.
  "EXPLORE",
  // Any magic action originating from within the Explore From Here editor.
  "EXPLORE_FROM_HERE",
  // Any magic action originating from Slack.
  "SLACK",
  // Any magic action originating from "Ask" (NOT Ask Magic)
  "ASK",
  // [SEMANTIC AUTHORING]
  // Any magic action originating from within the Semantic Authoring workbench.
  "SEMANTIC_AUTHORING",
  // Any magic action originating from the Eval / Research team.
  "EVAL",
  // When magic pauses saying "I can't do that", and the user types in a new prompt
  "UNSUPPORTED_PAUSE",
  // Any magic action that originates from the home page - this could include "Ask Magic"
  "HOME",
  // [DEPRECATED]
  "CONTINUE_FROM_CHAIN", // not used anywhere
  "TR_REGEN",
]);
export type MagicEventSource = z.infer<typeof MagicEventSourceLiteral>;
export const MagicEventSource = getNormalEnum(MagicEventSourceLiteral);

export const isExploreSource = (source: MagicEventSource | null): boolean =>
  source === "EXPLORE" || source === "EXPLORE_FROM_HERE";

export const MagicCellEditTypeLiteral = z.enum([
  "CUSTOM",
  "INSERT",
  "FIX_ERRORS",
  "BASIC_SYNTAX_FIX",
  "DOCUMENT",
  "FIX_SPELLING_AND_GRAMMAR",
]);
export type MagicCellEditType = z.infer<typeof MagicCellEditTypeLiteral>;
export const MagicCellEditType = getNormalEnum(MagicCellEditTypeLiteral);

export const MagicCellActionLiteral = z.enum(["NAME_CELL", "NAME_CELL_OUTPUT"]);
export type MagicCellAction = z.infer<typeof MagicCellActionLiteral>;
export const MagicCellAction = getNormalEnum(MagicCellActionLiteral);

export const MagicProjectActionLiteral = z.enum([
  "DESCRIBE_PROJECT",
  "NAME_PROJECT",
]);
export type MagicProjectAction = z.infer<typeof MagicProjectActionLiteral>;
export const MagicProjectAction = getNormalEnum(MagicProjectActionLiteral);

export const MagicSystemActionLiteral = z.enum(["HYDE_PROJECT"]);
export type MagicSystemAction = z.infer<typeof MagicSystemActionLiteral>;
export const MagicSystemAction = getNormalEnum(MagicSystemActionLiteral);

// Magic keywords exclusively associated with the response from
// a prompt template/workflow based run.
export const MagicPromptTemplateKeywordLiteral = z.enum([
  "SUMMARIZE_SCHEDULED_RUN",
  ...MagicCellEditTypeLiteral.options,
  ...MagicCellActionLiteral.options,
  ...MagicProjectActionLiteral.options,
  ...MagicSystemActionLiteral.options,
]);
export type MagicPromptTemplateKeyword = z.infer<
  typeof MagicPromptTemplateKeywordLiteral
>;
export const MagicPromptTemplateKeyword = getNormalEnum(
  MagicPromptTemplateKeywordLiteral,
);

// Magic keywords exclusively associated with the response from
// an agentic run.
export const MagicAgentKeywordLiteral = z.enum([
  "AGENT_RESPONSE",
  "AGENT_TOOL_CALL",
  "AGENT_COMPRESSION",
]);
export type MagicAgentKeyword = z.infer<typeof MagicAgentKeywordLiteral>;
export const MagicAgentKeyword = getNormalEnum(MagicAgentKeywordLiteral);

export const MagicKeywordLiteral = z.enum([
  ...MagicPromptTemplateKeywordLiteral.options,
  ...MagicAgentKeywordLiteral.options,
]);
export type MagicKeyword = z.infer<typeof MagicKeywordLiteral>;
export const MagicKeyword = getNormalEnum(MagicKeywordLiteral);

// Magic keywords that should not be reflected as a `latestMagicEvent` for a cell
export const ExcludedCellMagicKeywordLiteral = z.union([
  z.literal(MagicKeyword.NAME_CELL),
  z.literal(MagicKeyword.NAME_CELL_OUTPUT),
]);
export type ExcludedCellMagicKeyword = z.infer<
  typeof ExcludedCellMagicKeywordLiteral
>;
export const ExcludedCellMagicKeyword = getNormalEnum(
  ExcludedCellMagicKeywordLiteral,
);
export const SystemMagicEventStatusLiteral = z.enum([
  "CREATED",
  "PROCESSING",
  "FAILED",
  "COMPLETED",
]);
export type SystemMagicEventStatus = z.infer<
  typeof SystemMagicEventStatusLiteral
>;
export const SystemMagicEventStatus = getNormalEnum(
  SystemMagicEventStatusLiteral,
);

export const SystemMagicEventSourceLiteral = z.enum(["SERVER", "EVAL"]);
export type SystemMagicEventSource = z.infer<
  typeof SystemMagicEventSourceLiteral
>;
export const SystemMagicEventSource = getNormalEnum(
  SystemMagicEventSourceLiteral,
);
export const isEventPendingReview = (
  status?: MagicEventStatus | null,
): boolean =>
  status != null &&
  (status === MagicEventStatus.PENDING_REVIEW ||
    status === MagicEventStatus.ERROR_WITH_FALLBACK);

export const MagicEventStatusLiteral = z.enum([
  "LOADING",
  "ERROR",
  // Use this state when we fallback to a safe default, if the LLM generated result cannot be used (some error occurred).
  // For Explore, the fallback is generated at runtime, to maintain the event."result" for debugging and consistent with what the LLM responded with.
  "ERROR_WITH_FALLBACK",
  // The Magic Event is completed and potentially waiting user action to accept.
  "PENDING_REVIEW",
  "CANCELLED",
  // Used when an event is reviewed before applying - useful for Magic Analysis to 'accept' the new cells.
  "REVIEWED",
  // Indicates that the Magic Event previously failed and was retried. A new Magic Event would be generated.
  "RETRIED",
]);
export type MagicEventStatus = z.infer<typeof MagicEventStatusLiteral>;
export const MagicEventStatus = getNormalEnum(MagicEventStatusLiteral);

// #region Errors & Warnings

// Special failure events that we show as warnings
// in the UI because a partial result was still achieved.
export const MagicEventWarningLiteral = z.enum(["JOIN_VALIDATION_TIMEOUT"]);

export const MagicEventFailureLiteral = z.enum([
  "NO_CONTEXT",
  "BAD_DATA",
  "AGENT_ERROR",
  "AGENT_OVERLOADED",
  "AGENT_RATE_LIMITED",
  "GENERIC_ERROR",
  "POSSIBLE_PROMPT_INJECTION",
  "OVER_LIMIT",
  "NO_DATA",
  "INVALID_RESPONSE",
  "HALLUCINATED_SCHEMA_ELEMENT",
  "NON_EXPRESSIBLE",
  "INVALID_JOIN",
  "EXPLORE_TIMED_OUT",
  "EXPLORE_ERRORED",
  "EXPLORE_TABLE_SWAPPED",
  "UNSUPPORTED_QUERY_ELEMENTS", // agent wrote SQL for an explore that cannot be expressed in explore UX
  "EXPLORE_NO_DATA",
  "CHART_ROW_LIMIT_EXCEEDED",
  "BYOK_AUTHENTICATION", // Received an authentication error from the provider for a customer's key
  "BYOK_KEY_MISSING", // Org requires its own key (BYOK) but none is configured for the requested client type
  "AGENT_COMPRESSION_ERROR", // Error occurred while compressing the conversation
  "TOKEN_COUNTING_FAILURE", // Error occurred while counting tokens
  "REQUEST_CANCELLED", // Request was cancelled
  "INVALID_MESSAGE_HISTORY", // Message history is got corrupted
  "INVALID_REQUEST", // 400 error from provider due to malformed request (e.g., invalid base64)
  "DATA_CONNECTION_NOT_AUTHORIZED", // User lacks query access to a data connection the agent needs to use
  "MODEL_NOT_AVAILABLE", // Provider key/config can't access the requested model (e.g. a BYOK key that doesn't expose it)
  "PROVIDER_CONNECTION_INTERRUPTED", // Network/transport failure talking to the provider (e.g. ECONNRESET, read timeout, mid-stream socket close)
  "AGENT_AUTH", // Authentication/authorization error from the provider (e.g. a 401/403 for a bad, missing, or expired key)
  "PROVIDER_QUOTA_EXCEEDED", // The provider account is out of funds/quota (e.g. OpenAI insufficient_quota, Anthropic "credit balance is too low")
  "DEADLINE_EXCEEDED", // Our own deadline for a synchronous call (syncProviderTimeoutMs) passed before the provider finished. Says nothing about provider health
]);

export const MagicEventFailureReasonLiteral = z.enum([
  ...MagicEventFailureLiteral.options,
  ...MagicEventWarningLiteral.options,
]);

export type MagicEventFailureReason = z.infer<
  typeof MagicEventFailureReasonLiteral
>;
export const MagicEventFailureReason = getNormalEnum(
  MagicEventFailureReasonLiteral,
);

export const MagicCellRunResultLiteral = z.enum([
  "SUCCESS", // cell ran successfully
  "ERROR", // cell execution errored
  "TIMEOUT", // cell took too long to run
  "CANCELLED", // cell execution was cancelled (e.g. kernel interrupt)
  "NO_DATA", // sql cell returned no data
  "FAILURE", // failure that should cause the whole MA to error (e.g. no context)
]);
export type MagicCellRunResult = z.infer<typeof MagicCellRunResultLiteral>;
export const MagicCellRunResult = getNormalEnum(MagicCellRunResultLiteral);

export const TEXT_TOO_LONG_ERROR =
  "AI features only support up to ~3,000 words. Try splitting this cell into multiple cells.";

export const MODEL_OVERLOADED_ERROR =
  "Our model provider is having issues or is at capacity. Try again in a few minutes.";

export const AGENT_RATE_LIMITED_ERROR =
  "You've reached a temporary AI request limit. Wait a minute and try again.";

export const MAGIC_PROVIDER_ERROR =
  "Our model provider is having problems. We will be back as soon as they are!";

export const MAGIC_DEADLINE_EXCEEDED =
  "The model took too long to respond. Please try again.";

export const BYOK_AUTHENTICATION_ERROR =
  "We had a problem authorizing the request with your workspace's API key. Please reach out to an Admin to have them replace the credentials.";

export const BYOK_KEY_MISSING_ERROR =
  "This workspace is configured to use its own API key, but none is set up. Ask a workspace admin to add an API key in the workspace's model settings.";

export const GENERIC_MAGIC_ERROR =
  "Something went wrong. Please try again, or contact support if the problem persists.";

export const MAGIC_BAD_PROMPT_ERROR =
  "Your request couldn't be processed. Try rephrasing or providing more specific instructions.";

export const MAGIC_OVER_USAGE_ERROR =
  "You've reached your AI usage limit. Upgrade your plan to continue.";

export const MAGIC_NO_CONTEXT_ERROR =
  "No relevant data was found for your request. Try rephrasing or verifying the data source.";

export const MAGIC_NO_RESULTS =
  "SQL cell returned no data. Review your results before resuming.";

export const MAGIC_BAD_DATA =
  "Unable to create chart with the current data selection.";

export const MAGIC_INVALID_RESPONSE =
  "We couldn't generate a valid response for your request. Try rephrasing or providing more specific instructions.";

export const MAGIC_UNSUPPORTED_QUERY_ELEMENTS =
  "The provided request cannot be expressed in an Exploration.";

const MAGIC_JOIN_VALIDATION_ERROR =
  "There was an issue validating your join. Check column compatibility or try a different join condition.";

const MAGIC_JOIN_VALIDATION_TIMEOUT =
  "We were unable to validate this join. Double check your data's accuracy.";

const MAGIC_NO_SUMMARY_FROM_EXPLORE_TIMEOUT =
  "Your exploration took too long to run. We were unable to generate a summary.";

const MAGIC_GENERATED_EXPLORE_ERRORED =
  "We tried to generate an exploration, but the combination of filters and transformations applied resulted in an error. Try checking and adjusting the column assignments and filter values.";

const MAGIC_SUMMARY_FROM_EXPLORE_TABLE_SWAPPED =
  "We were unable to find relevant data to answer your question. Try picking a different data source.";

const MAGIC_EXPLORE_NO_DATA =
  "Your exploration returned no data. Consider removing a filter or changing your question.";

const MAGIC_EXPLORE_TOO_MANY_ROWS =
  "Your exploration returned too many rows of data. Consider adding a filter or changing your question.";

const MAGIC_AGENT_COMPRESSION_ERROR =
  "Context summarization failed. The most recent message may be too large to summarize. Please send another message to try again.";

const MAGIC_INVALID_MESSAGE_HISTORY =
  "There's an issue with your thread. Consider creating a new thread to continue.";

// This isn't the ideal error message, because we don't actually know what the token counting failure
// implies yet. Maybe it implies the provider is down, but I'm not totally sure. For now, we'll just
// show a generic error and we can update it later.
const MAGIC_TOKEN_COUNTING_FAILURE =
  "Something went wrong. Please try again, or contact support if the problem persists.";

const MAGIC_REQUEST_CANCELLED = "The request was cancelled.";

const MAGIC_DATA_CONNECTION_NOT_AUTHORIZED =
  "You don't have permission to use one of the data connections this app relies on. Contact the project owner or your workspace admin for access.";

const MAGIC_MODEL_NOT_AVAILABLE =
  "The selected model isn't available with this workspace's AI configuration. If your workspace uses its own API key (BYOK), ask an admin to confirm the key can access this model in the workspace's model settings. Otherwise, try selecting a different model.";

const MAGIC_PROVIDER_CONNECTION_INTERRUPTED =
  "The connection to the model provider was interrupted. This is usually temporary — please try again.";

const MAGIC_AGENT_AUTH =
  "We couldn't authenticate with the model provider. If your workspace uses its own API key (BYOK), ask an admin to verify it in the workspace's model settings, otherwise please contact support.";

const MAGIC_PROVIDER_QUOTA_EXCEEDED =
  "The model provider rejected the request because its account is out of credits or has hit a billing limit. If your workspace uses its own API key (BYOK), ask an admin to add funds or raise the limit in the provider's billing settings, otherwise please contact support.";

export const MagicErrorTypeMessageMap: Record<MagicEventFailureReason, string> =
  {
    [MagicEventFailureReason.AGENT_OVERLOADED]: MODEL_OVERLOADED_ERROR,
    [MagicEventFailureReason.AGENT_RATE_LIMITED]: AGENT_RATE_LIMITED_ERROR,
    [MagicEventFailureReason.GENERIC_ERROR]: GENERIC_MAGIC_ERROR,
    [MagicEventFailureReason.POSSIBLE_PROMPT_INJECTION]: MAGIC_BAD_PROMPT_ERROR,
    [MagicEventFailureReason.AGENT_ERROR]: MAGIC_PROVIDER_ERROR,
    [MagicEventFailureReason.NO_CONTEXT]: MAGIC_NO_CONTEXT_ERROR,
    [MagicEventFailureReason.OVER_LIMIT]: MAGIC_OVER_USAGE_ERROR,
    [MagicEventFailureReason.NO_DATA]: MAGIC_NO_RESULTS,
    [MagicEventFailureReason.BAD_DATA]: MAGIC_BAD_DATA,
    [MagicEventFailureReason.INVALID_RESPONSE]: MAGIC_INVALID_RESPONSE,
    [MagicEventFailureReason.UNSUPPORTED_QUERY_ELEMENTS]:
      MAGIC_UNSUPPORTED_QUERY_ELEMENTS,
    [MagicEventFailureReason.NON_EXPRESSIBLE]: MAGIC_UNSUPPORTED_QUERY_ELEMENTS,
    [MagicEventFailureReason.HALLUCINATED_SCHEMA_ELEMENT]: GENERIC_MAGIC_ERROR, // Should never be shown to the user
    [MagicEventFailureReason.JOIN_VALIDATION_TIMEOUT]:
      MAGIC_JOIN_VALIDATION_TIMEOUT,
    [MagicEventFailureReason.INVALID_JOIN]: MAGIC_JOIN_VALIDATION_ERROR,
    [MagicEventFailureReason.EXPLORE_TIMED_OUT]:
      MAGIC_NO_SUMMARY_FROM_EXPLORE_TIMEOUT,
    [MagicEventFailureReason.EXPLORE_ERRORED]: MAGIC_GENERATED_EXPLORE_ERRORED,
    [MagicEventFailureReason.EXPLORE_TABLE_SWAPPED]:
      MAGIC_SUMMARY_FROM_EXPLORE_TABLE_SWAPPED,
    [MagicEventFailureReason.EXPLORE_NO_DATA]: MAGIC_EXPLORE_NO_DATA,
    [MagicEventFailureReason.CHART_ROW_LIMIT_EXCEEDED]:
      MAGIC_EXPLORE_TOO_MANY_ROWS,
    [MagicEventFailureReason.BYOK_AUTHENTICATION]: BYOK_AUTHENTICATION_ERROR,
    [MagicEventFailureReason.BYOK_KEY_MISSING]: BYOK_KEY_MISSING_ERROR,
    [MagicEventFailureReason.AGENT_COMPRESSION_ERROR]:
      MAGIC_AGENT_COMPRESSION_ERROR,
    [MagicEventFailureReason.TOKEN_COUNTING_FAILURE]:
      MAGIC_TOKEN_COUNTING_FAILURE,
    [MagicEventFailureReason.REQUEST_CANCELLED]: MAGIC_REQUEST_CANCELLED,
    [MagicEventFailureReason.INVALID_MESSAGE_HISTORY]:
      MAGIC_INVALID_MESSAGE_HISTORY,
    [MagicEventFailureReason.INVALID_REQUEST]: GENERIC_MAGIC_ERROR,
    [MagicEventFailureReason.DATA_CONNECTION_NOT_AUTHORIZED]:
      MAGIC_DATA_CONNECTION_NOT_AUTHORIZED,
    [MagicEventFailureReason.MODEL_NOT_AVAILABLE]: MAGIC_MODEL_NOT_AVAILABLE,
    [MagicEventFailureReason.PROVIDER_CONNECTION_INTERRUPTED]:
      MAGIC_PROVIDER_CONNECTION_INTERRUPTED,
    [MagicEventFailureReason.AGENT_AUTH]: MAGIC_AGENT_AUTH,
    [MagicEventFailureReason.PROVIDER_QUOTA_EXCEEDED]:
      MAGIC_PROVIDER_QUOTA_EXCEEDED,
    [MagicEventFailureReason.DEADLINE_EXCEEDED]: MAGIC_DEADLINE_EXCEEDED,
  };

// #endregion Errors & Warnings

export const MagicTableReference = z.object({
  db: z.string().or(z.null()),
  schema: z.string(),
  table: z.string(),
});
export type MagicTableReference = z.infer<typeof MagicTableReference>;

export const MagicExploreTableInfo = z.object({
  baseTable: MagicTableReference,
  joinTables: z.array(MagicTableReference),
});
export type MagicExploreTableInfo = z.infer<typeof MagicExploreTableInfo>;

export const MagicHydeQuestion = z.string();
export type MagicHydeQuestion = z.infer<typeof MagicHydeQuestion>;

// NB - this is part of the function payload sent to the LLM; use caution if modifying,
// as it could affect response quality
export const MentionedDataframeNameSchema = z.array(
  z.object({ name: z.string(), type: z.string() }),
);
export type MentionedDataframeNameSchema = z.infer<
  typeof MentionedDataframeNameSchema
>;

export const VariableSpec = z.object({
  name: z.string(),
  type: z.string(),
  dataframeSchema: MentionedDataframeNameSchema.optional(),
});
export type VariableSpec = z.infer<typeof VariableSpec>;

export const MagicPromptGenerationCellSpec = z.object({
  type: MagicCellTypeLiteral,
  cellTitle: z.string(),
  inputVariables: z.array(VariableSpec),
  outputVariables: z.array(VariableSpec),
  prompt: z.string(),
});

export type MagicPromptGenerationCellSpec = z.infer<
  typeof MagicPromptGenerationCellSpec
>;

export const HexMagicApiKeySentinel = "HEX_DEFAULT" as const;
export type HexMagicApiKeySentinel = typeof HexMagicApiKeySentinel;

export const MagicEventMagicApiKey = z.union([
  z.literal(HexMagicApiKeySentinel),
  MagicApiKeyId,
]);
export type MagicEventMagicApiKey = z.infer<typeof MagicEventMagicApiKey>;

export const RealAiClientTypeLiteral = z.enum([
  "OPENAI",
  "ANTHROPIC",
  /**
   * Streaming OpenAI Chat Completions API (`/v1/chat/completions`).
   */
  "OPENAI_CHAT_COMPLETIONS",
  /**
   * Non-streaming OpenAI Chat Completions API (`/v1/chat/completions`).
   */
  "OPENAI_TYPEAHEAD",
]);
export type RealAiClientType = z.infer<typeof RealAiClientTypeLiteral>;
export const RealAiClientType = getNormalEnum(RealAiClientTypeLiteral);

export const AiClientTypeLiteral = z.enum([
  ...RealAiClientTypeLiteral.options,
  "MOCK",
]);
/**
 * Represents the different kinds of clients we support for AI calls.
 *
 * This is **not** necessarily the provider. For example, a AiClientType.OPENAI
 * does not mean we are using OpenAI as the provider, it just means we are using the OpenAI
 * wire format but the actual request could be handled by Baseten as a provider.
 */
export type AiClientType = z.infer<typeof AiClientTypeLiteral>;
export const AiClientType = getNormalEnum(AiClientTypeLiteral);

export const RealAiProviderTypeLiteral = z.enum([
  "OPENAI",
  "ANTHROPIC",
  "BASETEN",
  "BEDROCK",
  /**
   * VERTEX = Gemini Enterprise Agent Platform (formerly Vertex AI).
   * This value gets persisted in MagicApiKey.providerName so would need a migration to update
   */
  "VERTEX",
]);
export type RealAiProviderType = z.infer<typeof RealAiProviderTypeLiteral>;
export const RealAiProviderType = getNormalEnum(RealAiProviderTypeLiteral);

export const AiProviderTypeLiteral = z.enum([
  ...RealAiProviderTypeLiteral.options,
  "MOCK",
]);
/**
 * The company/platform that services a given AI request.
 */
export type AiProviderType = z.infer<typeof AiProviderTypeLiteral>;
export const AiProviderType = getNormalEnum(AiProviderTypeLiteral);

/**
 * Maps an AI client config to the provider that services it by default (i.e. when
 * the org has no BYOK key that overrides the provider).
 *
 * The existence of this utility is a little bit backwards, ideally this would
 * be done on the path we are actually resolving the credentials to use.
 */
export function defaultAiProviderForClientConfig({
  clientConfig,
}: {
  clientConfig: AiClientConfig;
}): AiProviderType {
  switch (clientConfig.type) {
    case AiClientType.OPENAI:
      return AiProviderType.OPENAI;
    case AiClientType.OPENAI_TYPEAHEAD:
      return AiProviderType.BASETEN;
    case AiClientType.OPENAI_CHAT_COMPLETIONS: {
      switch (clientConfig.params.model.gateway) {
        case ChatCompletionsGateway.BASETEN:
        case ChatCompletionsGateway.BASETEN_DEDICATED:
        case undefined:
          return AiProviderType.BASETEN;
        case ChatCompletionsGateway.BEDROCK:
          return AiProviderType.BEDROCK;
        default:
          return assertNever(
            clientConfig.params.model.gateway,
            clientConfig.params.model.gateway,
          );
      }
    }
    case AiClientType.ANTHROPIC:
      return AiProviderType.ANTHROPIC;
    case AiClientType.MOCK:
      return AiProviderType.MOCK;
    default:
      return assertNever(
        clientConfig,
        (clientConfig as { type?: string }).type,
      );
  }
}

export function aiProviderSupportsClientType({
  clientType,
  providerType,
}: {
  clientType: AiClientType;
  providerType: AiProviderType;
}): boolean {
  switch (clientType) {
    case AiClientType.ANTHROPIC: {
      switch (providerType) {
        case AiProviderType.ANTHROPIC:
        case AiProviderType.BEDROCK:
        case AiProviderType.VERTEX:
          return true;
        case AiProviderType.OPENAI:
        case AiProviderType.BASETEN:
        case AiProviderType.MOCK:
          return false;
        default:
          guardNever(providerType, providerType);
          return false;
      }
    }

    case AiClientType.OPENAI: {
      switch (providerType) {
        case AiProviderType.ANTHROPIC:
        case AiProviderType.BEDROCK:
        case AiProviderType.VERTEX:
        case AiProviderType.MOCK:
        case AiProviderType.BASETEN:
          return false;
        case AiProviderType.OPENAI:
          return true;
        default:
          guardNever(providerType, providerType);
          return false;
      }
    }

    case AiClientType.OPENAI_CHAT_COMPLETIONS:
    case AiClientType.OPENAI_TYPEAHEAD: {
      switch (providerType) {
        case AiProviderType.ANTHROPIC:
        case AiProviderType.BEDROCK:
        case AiProviderType.VERTEX:
        case AiProviderType.MOCK:
        case AiProviderType.OPENAI:
          return false;
        case AiProviderType.BASETEN:
          return true;
        default:
          guardNever(providerType, providerType);
          return false;
      }
    }

    case AiClientType.MOCK: {
      if (providerType === AiProviderType.MOCK) {
        return true;
      } else {
        return false;
      }
    }

    default:
      guardNever(clientType, clientType);
      return false;
  }
}

/**
 * Maps AI client types to their canonical client types for legacy code paths that key
 * on a smaller `OPENAI | ANTHROPIC` set (e.g. prompt-template
 * variants).
 *
 * chat completions and typeahead uses OpenAI clients under the hood
 */
export function resolveAiClientType(
  clientType: RealAiClientType,
): "BASETEN" | "OPENAI" | "ANTHROPIC" {
  switch (clientType) {
    case AiClientType.OPENAI_CHAT_COMPLETIONS:
      return "OPENAI";
    case AiClientType.OPENAI_TYPEAHEAD:
      return "BASETEN";
    default:
      return clientType;
  }
}

export const StreamingKey = z.union([
  z.object({
    key: z.string(),
    type: z.literal("STRING"),
  }),
  z.object({ key: z.array(z.string()), type: z.literal("MULTI_OBJECT") }),
  z.object({
    key: z.string(),
    type: z.literal("OBJECT_ARRAY"),
  }),
]);
export type StreamingKey = z.infer<typeof StreamingKey>;

export const MockToolCalls = z.array(
  z
    .object({
      arguments: zAnyNonNullish(),
      name: z.string(),
    })
    .and(
      z
        .object({
          thinking: z.array(z.string()).optional(),
        })
        .partial(),
    ),
);
export const MockFunction = z.object({
  streamingKey: StreamingKey.optional(),
  function: z.record(z.string(), z.unknown()),
});
const StringOrFunction = z.string().or(MockFunction);
export const StringOrFunctionOrFailureType = z.union([
  StringOrFunction,
  MagicEventFailureReasonLiteral,
]);
export type StringOrFunctionOrFailureType = z.infer<
  typeof StringOrFunctionOrFailureType
>;
export const MockResponse = z.union([
  StringOrFunction,
  MagicEventFailureReasonLiteral,
  MockToolCalls,
]);
export type MockResponse = z.infer<typeof MockResponse>;

/**
 * A group of mock responses that will be dispatched concurrently by the caller
 * (e.g. via `Promise.all`). The mock provider picks the matching entry by the
 * requested tool name instead of FIFO order, so the queue stays deterministic
 * even when two parallel `callLLM` invocations race to consume it.
 *
 * Falls back to FIFO within the group when no entry has a matching tool name
 * (e.g. for plain-text responses).
 */
export const MockResponseParallelGroup = z.object({
  parallel: z.array(MockResponse),
});
export type MockResponseParallelGroup = z.infer<
  typeof MockResponseParallelGroup
>;

/**
 * A single entry in `responseQueue`. Most entries are plain `MockResponse`s
 * (consumed FIFO). Use `{ parallel: [...] }` to denote a set of responses that
 * are awaited concurrently in production code and should therefore be
 * dispatched by tool name instead of arrival order.
 */
export const MockResponseQueueEntry = z.union([
  MockResponse,
  MockResponseParallelGroup,
]);
export type MockResponseQueueEntry = z.infer<typeof MockResponseQueueEntry>;

export const MockClientModelParams = z
  .object({
    model: z.enum(["MOCK_PRIMARY_MODEL", "MOCK_FALLBACK_MODEL"]),
    sleepTime: z.number().optional(),
    /**
     * Map prompt template type to a response. Use for
     * one-shot prompts.
     */
    responseMap: zKeyedRecord(
      PromptTemplateTypeLiteral,
      z
        .union([
          StringOrFunctionOrFailureType,
          z.record(z.string(), StringOrFunctionOrFailureType),
        ])
        .optional(),
    ),
  })
  .and(
    z
      .object({
        /**
         * Response queue, agent will make these calls in order. Use for agents.
         *
         * Use `{ parallel: [response1, response2] }` for sets of responses that
         * are awaited concurrently in production code (e.g. via `Promise.all`).
         * The mock dispatches within a parallel group by tool name so the queue
         * doesn't depend on which arm of the `Promise.all` happens to call the
         * mock first.
         */
        responseQueue: z.array(MockResponseQueueEntry),
      })
      .partial(),
  );
export type MockClientModelParams = z.infer<typeof MockClientModelParams>;

const AiClientConfigBase = z.object({
  type: AiClientTypeLiteral,
});

export const MockAiClientConfig = AiClientConfigBase.extend({
  type: z.literal(AiClientType.MOCK),
  params: MockClientModelParams,
});
export type MockAiClientConfig = z.infer<typeof MockAiClientConfig>;

export const OpenAiClientConfig = AiClientConfigBase.extend({
  type: z.literal(AiClientType.OPENAI),
  params: OpenAiModelParams,
});
export type OpenAiClientConfig = z.infer<typeof OpenAiClientConfig>;

export const OpenAiChatCompletionsClientConfig = AiClientConfigBase.extend({
  type: z.literal(AiClientType.OPENAI_CHAT_COMPLETIONS),
  params: ChatCompletionsModelParams,
});
export type OpenAiChatCompletionsClientConfig = z.infer<
  typeof OpenAiChatCompletionsClientConfig
>;

export const AnthropicClientConfig = AiClientConfigBase.extend({
  type: z.literal(AiClientType.ANTHROPIC),
  params: AnthropicModelParams,
});
export type AnthropicClientConfig = z.infer<typeof AnthropicClientConfig>;

export const OpenAITypeAheadClientConfig = z.object({
  modelKeys: z.object({
    us: z.string(),
    eu: z.string(),
    dev: z.string(),
  }),
  maxInputTokens: z.number(),
  maxOutputTokens: z.number().optional(), // Not used by reasoning models
});
export type OpenAITypeAheadClientConfig = z.infer<
  typeof OpenAITypeAheadClientConfig
>;

// Should approximately match the OpenAI model params,
// but with a different model type
export const BasetenLLMModelParams = z.object({
  frequencyPenalty: z.number(),
  n: z.number(),
  presencePenalty: z.number(),
  stopWords: z.array(z.string()),
  temperature: z.number().optional(), // Not used by reasoning models
  topP: z.number(),
  reasoningEffort: z.enum(["low", "medium", "high"]).optional(), // ONLY used by reasoning models
  model: OpenAITypeAheadClientConfig,
});
export type BasetenLLMModelParams = z.infer<typeof BasetenLLMModelParams>;

export const OpenAiTypeaheadClientConfig = AiClientConfigBase.extend({
  type: z.literal(AiClientType.OPENAI_TYPEAHEAD),
  params: BasetenLLMModelParams,
});
export type OpenAiTypeaheadClientConfig = z.infer<
  typeof OpenAiTypeaheadClientConfig
>;

export const AiClientConfig = z.union([
  AnthropicClientConfig,
  MockAiClientConfig,
  OpenAiClientConfig,
  OpenAiChatCompletionsClientConfig,
  OpenAiTypeaheadClientConfig,
]);
export type AiClientConfig = z.infer<typeof AiClientConfig>;

export const AiStaticClientConfigNameLiteral = z.enum([
  "OPENAI_GPT_56",
  "OPENAI_GPT_56_LOW_REASONING",
  "OPENAI_GPT_56_LOW_REASONING_FAST",
  "OPENAI_GPT_56_MEDIUM_REASONING",
  "OPENAI_GPT_56_MEDIUM_REASONING_FAST",
  "OPENAI_GPT_56_HIGH_REASONING",
  "OPENAI_GPT_56_HIGH_REASONING_FAST",
  "OPENAI_GPT_56_XHIGH_REASONING",
  "OPENAI_GPT_56_XHIGH_REASONING_FAST",
  "OPENAI_GPT_56_MAX_REASONING",
  "OPENAI_GPT_56_MAX_REASONING_FAST",
  "OPENAI_GPT_56_TERRA",
  "OPENAI_GPT_56_TERRA_LOW_REASONING",
  "OPENAI_GPT_56_TERRA_MEDIUM_REASONING",
  "OPENAI_GPT_56_TERRA_HIGH_REASONING",
  "OPENAI_GPT_56_TERRA_XHIGH_REASONING",
  "OPENAI_GPT_56_TERRA_MAX_REASONING",
  "OPENAI_GPT_56_LUNA",
  "OPENAI_GPT_56_LUNA_LOW_REASONING",
  "OPENAI_GPT_56_LUNA_MEDIUM_REASONING",
  "OPENAI_GPT_56_LUNA_HIGH_REASONING",
  "OPENAI_GPT_56_LUNA_XHIGH_REASONING",
  "OPENAI_GPT_56_LUNA_MAX_REASONING",
  "OPENAI_GPT_6_ASTRA",
  "OPENAI_GPT_6_ASTRA_LOW_REASONING",
  "OPENAI_GPT_6_ASTRA_LOW_REASONING_FAST",
  "OPENAI_GPT_6_ASTRA_MEDIUM_REASONING",
  "OPENAI_GPT_6_ASTRA_MEDIUM_REASONING_FAST",
  "OPENAI_GPT_6_ASTRA_HIGH_REASONING",
  "OPENAI_GPT_6_ASTRA_HIGH_REASONING_FAST",
  "OPENAI_GPT_6_ASTRA_XHIGH_REASONING",
  "OPENAI_GPT_6_ASTRA_XHIGH_REASONING_FAST",
  "OPENAI_GPT_6_ASTRA_MAX_REASONING",
  "OPENAI_GPT_6_ASTRA_MAX_REASONING_FAST",
  "OPENAI_GPT_6_SOL",
  "OPENAI_GPT_6_SOL_LOW_REASONING",
  "OPENAI_GPT_6_SOL_LOW_REASONING_FAST",
  "OPENAI_GPT_6_SOL_MEDIUM_REASONING",
  "OPENAI_GPT_6_SOL_MEDIUM_REASONING_FAST",
  "OPENAI_GPT_6_SOL_HIGH_REASONING",
  "OPENAI_GPT_6_SOL_HIGH_REASONING_FAST",
  "OPENAI_GPT_6_SOL_XHIGH_REASONING",
  "OPENAI_GPT_6_SOL_XHIGH_REASONING_FAST",
  "OPENAI_GPT_6_SOL_MAX_REASONING",
  "OPENAI_GPT_6_SOL_MAX_REASONING_FAST",
  "OPENAI_GPT_61_SOL",
  "OPENAI_GPT_61_SOL_LOW_REASONING",
  "OPENAI_GPT_61_SOL_LOW_REASONING_FAST",
  "OPENAI_GPT_61_SOL_MEDIUM_REASONING",
  "OPENAI_GPT_61_SOL_MEDIUM_REASONING_FAST",
  "OPENAI_GPT_61_SOL_HIGH_REASONING",
  "OPENAI_GPT_61_SOL_HIGH_REASONING_FAST",
  "OPENAI_GPT_61_SOL_XHIGH_REASONING",
  "OPENAI_GPT_61_SOL_XHIGH_REASONING_FAST",
  "OPENAI_GPT_61_SOL_MAX_REASONING",
  "OPENAI_GPT_61_SOL_MAX_REASONING_FAST",
  "OPENAI_GPT_6_LUNA",
  "OPENAI_GPT_6_LUNA_LOW_REASONING",
  "OPENAI_GPT_6_LUNA_LOW_REASONING_FAST",
  "OPENAI_GPT_6_LUNA_MEDIUM_REASONING",
  "OPENAI_GPT_6_LUNA_MEDIUM_REASONING_FAST",
  "OPENAI_GPT_6_LUNA_HIGH_REASONING",
  "OPENAI_GPT_6_LUNA_HIGH_REASONING_FAST",
  "OPENAI_GPT_6_LUNA_XHIGH_REASONING",
  "OPENAI_GPT_6_LUNA_XHIGH_REASONING_FAST",
  "ANTHROPIC_CLAUDE_4_5_HAIKU",
  "ANTHROPIC_CLAUDE_4_5_HAIKU_THINKING",
  "ANTHROPIC_CLAUDE_5_5_HAIKU",
  "ANTHROPIC_CLAUDE_5_5_HAIKU_LOW_EFFORT",
  "ANTHROPIC_CLAUDE_5_5_HAIKU_MEDIUM_EFFORT",
  "ANTHROPIC_CLAUDE_5_5_HAIKU_HIGH_EFFORT",
  "ANTHROPIC_CLAUDE_5_5_HAIKU_XHIGH_EFFORT",
  "ANTHROPIC_CLAUDE_5_5_HAIKU_MAX_EFFORT",
  "ANTHROPIC_CLAUDE_4_6_OPUS",
  "ANTHROPIC_CLAUDE_4_6_OPUS_THINKING",
  "ANTHROPIC_CLAUDE_4_6_OPUS_LOW_EFFORT_THINKING",
  "ANTHROPIC_CLAUDE_4_6_OPUS_MEDIUM_EFFORT_THINKING",
  "ANTHROPIC_CLAUDE_4_6_OPUS_HIGH_EFFORT_THINKING",
  "ANTHROPIC_CLAUDE_4_6_OPUS_MAX_EFFORT_THINKING",
  "ANTHROPIC_CLAUDE_4_8_OPUS",
  "ANTHROPIC_CLAUDE_4_8_OPUS_LOW_EFFORT",
  "ANTHROPIC_CLAUDE_4_8_OPUS_LOW_EFFORT_FAST",
  "ANTHROPIC_CLAUDE_4_8_OPUS_LOW_EFFORT_NO_THINKING",
  "ANTHROPIC_CLAUDE_4_8_OPUS_MEDIUM_EFFORT",
  "ANTHROPIC_CLAUDE_4_8_OPUS_MEDIUM_EFFORT_FAST",
  "ANTHROPIC_CLAUDE_4_8_OPUS_MEDIUM_EFFORT_NO_THINKING",
  "ANTHROPIC_CLAUDE_4_8_OPUS_HIGH_EFFORT",
  "ANTHROPIC_CLAUDE_4_8_OPUS_HIGH_EFFORT_FAST",
  "ANTHROPIC_CLAUDE_4_8_OPUS_XHIGH_EFFORT",
  "ANTHROPIC_CLAUDE_4_8_OPUS_XHIGH_EFFORT_FAST",
  "ANTHROPIC_CLAUDE_4_8_OPUS_MAX_EFFORT",
  "ANTHROPIC_CLAUDE_4_8_OPUS_MAX_EFFORT_FAST",
  "ANTHROPIC_CLAUDE_OPUS_5",
  "ANTHROPIC_CLAUDE_OPUS_5_LOW_EFFORT",
  "ANTHROPIC_CLAUDE_OPUS_5_LOW_EFFORT_FAST",
  "ANTHROPIC_CLAUDE_OPUS_5_LOW_EFFORT_NO_THINKING",
  "ANTHROPIC_CLAUDE_OPUS_5_MEDIUM_EFFORT",
  "ANTHROPIC_CLAUDE_OPUS_5_MEDIUM_EFFORT_FAST",
  "ANTHROPIC_CLAUDE_OPUS_5_MEDIUM_EFFORT_NO_THINKING",
  "ANTHROPIC_CLAUDE_OPUS_5_HIGH_EFFORT",
  "ANTHROPIC_CLAUDE_OPUS_5_HIGH_EFFORT_FAST",
  "ANTHROPIC_CLAUDE_OPUS_5_XHIGH_EFFORT",
  "ANTHROPIC_CLAUDE_OPUS_5_XHIGH_EFFORT_FAST",
  "ANTHROPIC_CLAUDE_OPUS_5_MAX_EFFORT",
  "ANTHROPIC_CLAUDE_OPUS_5_MAX_EFFORT_FAST",
  "ANTHROPIC_CLAUDE_OPUS_5_5",
  "ANTHROPIC_CLAUDE_OPUS_5_5_LOW_EFFORT",
  "ANTHROPIC_CLAUDE_OPUS_5_5_LOW_EFFORT_FAST",
  "ANTHROPIC_CLAUDE_OPUS_5_5_MEDIUM_EFFORT",
  "ANTHROPIC_CLAUDE_OPUS_5_5_MEDIUM_EFFORT_FAST",
  "ANTHROPIC_CLAUDE_OPUS_5_5_HIGH_EFFORT",
  "ANTHROPIC_CLAUDE_OPUS_5_5_HIGH_EFFORT_FAST",
  "ANTHROPIC_CLAUDE_OPUS_5_5_XHIGH_EFFORT",
  "ANTHROPIC_CLAUDE_OPUS_5_5_XHIGH_EFFORT_FAST",
  "ANTHROPIC_CLAUDE_OPUS_5_5_MAX_EFFORT",
  "ANTHROPIC_CLAUDE_OPUS_5_5_MAX_EFFORT_FAST",
  "ANTHROPIC_CLAUDE_FABLE_5",
  "ANTHROPIC_CLAUDE_FABLE_5_LOW_EFFORT",
  "ANTHROPIC_CLAUDE_FABLE_5_LOW_EFFORT_NO_THINKING",
  "ANTHROPIC_CLAUDE_FABLE_5_MEDIUM_EFFORT",
  "ANTHROPIC_CLAUDE_FABLE_5_MEDIUM_EFFORT_NO_THINKING",
  "ANTHROPIC_CLAUDE_FABLE_5_HIGH_EFFORT",
  "ANTHROPIC_CLAUDE_FABLE_5_MAX_EFFORT",
  "ANTHROPIC_CLAUDE_FABLE_5_1",
  "ANTHROPIC_CLAUDE_FABLE_5_1_LOW_EFFORT",
  "ANTHROPIC_CLAUDE_FABLE_5_1_MEDIUM_EFFORT",
  "ANTHROPIC_CLAUDE_FABLE_5_1_HIGH_EFFORT",
  "ANTHROPIC_CLAUDE_FABLE_5_1_XHIGH_EFFORT",
  "ANTHROPIC_CLAUDE_FABLE_5_1_MAX_EFFORT",
  "ANTHROPIC_CLAUDE_5_SONNET",
  "ANTHROPIC_CLAUDE_5_SONNET_LOW_EFFORT",
  "ANTHROPIC_CLAUDE_5_SONNET_LOW_EFFORT_NO_THINKING",
  "ANTHROPIC_CLAUDE_5_SONNET_MEDIUM_EFFORT",
  "ANTHROPIC_CLAUDE_5_SONNET_MEDIUM_EFFORT_NO_THINKING",
  "ANTHROPIC_CLAUDE_5_SONNET_HIGH_EFFORT",
  "ANTHROPIC_CLAUDE_5_SONNET_XHIGH_EFFORT",
  "ANTHROPIC_CLAUDE_5_SONNET_MAX_EFFORT",
  "ANTHROPIC_CLAUDE_5_5_SONNET",
  "ANTHROPIC_CLAUDE_5_5_SONNET_LOW_EFFORT",
  "ANTHROPIC_CLAUDE_5_5_SONNET_MEDIUM_EFFORT",
  "ANTHROPIC_CLAUDE_5_5_SONNET_HIGH_EFFORT",
  "ANTHROPIC_CLAUDE_5_5_SONNET_XHIGH_EFFORT",
  "ANTHROPIC_CLAUDE_5_5_SONNET_MAX_EFFORT",
  "BASETEN_DEDICATED_KIMI_K_2_7_THINKING",
  "BASETEN_DEDICATED_KIMI_K_2_7_THINKING_THROUGHPUT_OPTIMIZED",
  "BASETEN_DEDICATED_QWEN_3_6_35B_A3B",
  "BEDROCK_GROK_4_6_LOW_EFFORT",
  "BEDROCK_GROK_4_6_MEDIUM_EFFORT",
  "BEDROCK_GROK_4_6_HIGH_EFFORT",
  "BEDROCK_GROK_4_6_XHIGH_EFFORT",
  "BASETEN_DEFAULT_TYPEAHEAD",
]);
export type AiStaticClientConfigName = z.infer<
  typeof AiStaticClientConfigNameLiteral
>;
export const AiStaticClientConfigName = getNormalEnum(
  AiStaticClientConfigNameLiteral,
);

export const ModelFeatureKeyLiteral = z.enum([
  "logic-view-agent",
  "ask-agent",
  "review-agent",
  "semantic-authoring-agent",
  "tao-project-agent",
  "data-discovery-agent",
  "default-one-shot",
  "fix-code-basic",
  "standard-llm-rubric",
  "confusion-detection",
  "retrieval-failure-detection",
  "hex-content-generation",
  "typeahead",
  "generate-suggested-prompts-sync",
  "app-agent",
  "thread-topic-classification",
  "suggestion-source-grouping",
  "visualization-subagent",
  "data-app-subagent",
  "memory-extraction",
  "project-and-thread-naming",
  "subagent-work-summarization",
  "user-context-exhaust-detection",
  "thread-artifact-extraction",
  "knowledge-curation-agent",
  "customer-eval-judge-thread",
  "customer-eval-judge-final-answer",
  "customer-eval-numeric-extraction",
  "chart-fast-edit",
  // Used for testing
  "mock-agent",
]);

export type ModelFeatureKey = z.infer<typeof ModelFeatureKeyLiteral>;
export const ModelFeatureKey = getNormalEnum(ModelFeatureKeyLiteral);

export const UserFacingModelEntry = z.union([
  z.object({ kind: z.literal("model"), model: z.string() }),
  z.object({ kind: z.literal("comingSoon"), model: z.string() }),
]);

export const ModelFeatureConfig = z.object({
  features: z.record(
    z.string(),
    z.array(
      z.union([AiClientConfig, AiStaticClientConfigNameLiteral, z.string()]),
    ),
  ),
  // Ordered list of picker entries. Each entry is either a selectable model or a
  // coming-soon row; both resolve against USER_FACING_MODEL_CONFIGS by Model.
  // Authoring order is preserved as render order.
  userFacingModels: z.array(UserFacingModelEntry).optional(),
});
/**
 * Manually narrow the compile-time type to be more specific and only allow
 * keys in the features object to be ModelFeatureKey's. This is necessary
 * because Runtypes would fail to validate nested extra keys in a dictionary
 * defined based on a literal string union, so we broaden the runtype to allow
 * any string key, and then manually narrow the typescript type to ensure
 * compile-time compliance.
 */
export type ModelFeatureConfig = z.infer<typeof ModelFeatureConfig> & {
  features: Record<
    ModelFeatureKey,
    Array<AiClientConfig | AiStaticClientConfigName>
  > &
    // Add a catch-all for unknown keys to ensure this type is as correct as possible
    Record<string, unknown>;
};

export const DEFAULT_MODEL_FEATURE_STATIC_CONFIGS: Record<
  ModelFeatureKey,
  AiStaticClientConfigName[]
> = {
  "app-agent": [
    "ANTHROPIC_CLAUDE_OPUS_5_5_LOW_EFFORT",
    "OPENAI_GPT_6_LUNA_XHIGH_REASONING",
  ],
  "ask-agent": [
    "ANTHROPIC_CLAUDE_OPUS_5_5_LOW_EFFORT",
    "OPENAI_GPT_6_LUNA_XHIGH_REASONING",
  ],
  "tao-project-agent": [
    "OPENAI_GPT_56_LUNA_MEDIUM_REASONING",
    "ANTHROPIC_CLAUDE_5_SONNET_MEDIUM_EFFORT",
  ],
  "review-agent": [
    "OPENAI_GPT_6_LUNA_HIGH_REASONING",
    "ANTHROPIC_CLAUDE_5_5_SONNET_MEDIUM_EFFORT",
  ],
  "confusion-detection": [
    "OPENAI_GPT_6_LUNA_HIGH_REASONING",
    "ANTHROPIC_CLAUDE_5_SONNET_LOW_EFFORT_NO_THINKING",
  ],
  "data-discovery-agent": [
    "OPENAI_GPT_6_LUNA_HIGH_REASONING",
    "ANTHROPIC_CLAUDE_5_5_SONNET_MEDIUM_EFFORT",
  ],
  "chart-fast-edit": [
    "OPENAI_GPT_6_LUNA_LOW_REASONING",
    "ANTHROPIC_CLAUDE_5_SONNET_LOW_EFFORT_NO_THINKING",
  ],
  "default-one-shot": [
    "OPENAI_GPT_6_LUNA_HIGH_REASONING",
    "ANTHROPIC_CLAUDE_5_5_SONNET_MEDIUM_EFFORT",
  ],
  "fix-code-basic": [
    "OPENAI_GPT_6_LUNA_HIGH_REASONING",
    "ANTHROPIC_CLAUDE_5_5_SONNET_MEDIUM_EFFORT",
  ],
  "generate-suggested-prompts-sync": [
    "OPENAI_GPT_6_LUNA_HIGH_REASONING",
    "ANTHROPIC_CLAUDE_5_SONNET_LOW_EFFORT_NO_THINKING",
  ],
  "hex-content-generation": [
    "OPENAI_GPT_6_LUNA_HIGH_REASONING",
    "ANTHROPIC_CLAUDE_5_SONNET_LOW_EFFORT_NO_THINKING",
  ],
  "logic-view-agent": [
    "ANTHROPIC_CLAUDE_OPUS_5_5_LOW_EFFORT",
    "OPENAI_GPT_6_LUNA_XHIGH_REASONING",
  ],
  "memory-extraction": [
    "OPENAI_GPT_6_LUNA_LOW_REASONING",
    "ANTHROPIC_CLAUDE_5_5_SONNET_MEDIUM_EFFORT",
  ],
  "project-and-thread-naming": [
    "OPENAI_GPT_6_LUNA_HIGH_REASONING",
    "ANTHROPIC_CLAUDE_5_5_SONNET_MEDIUM_EFFORT",
  ],
  "mock-agent": [],
  "semantic-authoring-agent": [
    "ANTHROPIC_CLAUDE_OPUS_5_5_LOW_EFFORT",
    "OPENAI_GPT_6_LUNA_XHIGH_REASONING",
  ],
  "standard-llm-rubric": [
    "OPENAI_GPT_6_LUNA_HIGH_REASONING",
    "ANTHROPIC_CLAUDE_5_SONNET_LOW_EFFORT_NO_THINKING",
  ],
  "suggestion-source-grouping": [
    "OPENAI_GPT_6_LUNA_HIGH_REASONING",
    "ANTHROPIC_CLAUDE_5_SONNET_LOW_EFFORT_NO_THINKING",
  ],
  "retrieval-failure-detection": [
    "OPENAI_GPT_6_LUNA_HIGH_REASONING",
    "ANTHROPIC_CLAUDE_5_SONNET_LOW_EFFORT_NO_THINKING",
  ],
  "thread-topic-classification": [
    "OPENAI_GPT_6_LUNA_HIGH_REASONING",
    "ANTHROPIC_CLAUDE_5_SONNET_LOW_EFFORT_NO_THINKING",
  ],
  typeahead: ["BASETEN_DEFAULT_TYPEAHEAD"],
  "visualization-subagent": [
    "ANTHROPIC_CLAUDE_OPUS_5_5_LOW_EFFORT",
    "OPENAI_GPT_56_LOW_REASONING",
  ],
  "data-app-subagent": [
    "ANTHROPIC_CLAUDE_4_8_OPUS_LOW_EFFORT",
    "OPENAI_GPT_56_LOW_REASONING",
  ],
  "subagent-work-summarization": [
    "OPENAI_GPT_6_LUNA_HIGH_REASONING",
    "ANTHROPIC_CLAUDE_5_5_SONNET_MEDIUM_EFFORT",
  ],
  "user-context-exhaust-detection": [
    "ANTHROPIC_CLAUDE_4_6_OPUS",
    "OPENAI_GPT_56",
  ],
  "thread-artifact-extraction": [
    "OPENAI_GPT_56_LUNA_MEDIUM_REASONING",
    "BASETEN_DEDICATED_KIMI_K_2_7_THINKING_THROUGHPUT_OPTIMIZED",
    "ANTHROPIC_CLAUDE_4_6_OPUS",
    "OPENAI_GPT_56",
  ],
  "knowledge-curation-agent": [
    "OPENAI_GPT_56_HIGH_REASONING",
    "ANTHROPIC_CLAUDE_OPUS_5_MEDIUM_EFFORT",
  ],
  "customer-eval-judge-thread": [
    "OPENAI_GPT_56_MEDIUM_REASONING",
    "ANTHROPIC_CLAUDE_5_SONNET_MEDIUM_EFFORT_NO_THINKING",
  ],
  "customer-eval-judge-final-answer": [
    "OPENAI_GPT_56_LOW_REASONING",
    "ANTHROPIC_CLAUDE_5_SONNET_LOW_EFFORT_NO_THINKING",
  ],
  "customer-eval-numeric-extraction": [
    "OPENAI_GPT_6_LUNA_HIGH_REASONING",
    "ANTHROPIC_CLAUDE_5_SONNET_LOW_EFFORT_NO_THINKING",
  ],
};

export const DEFAULT_USER_FACING_MODELS: z.infer<
  typeof UserFacingModelEntry
>[] = [
  { kind: "model", model: Model.CLAUDE_OPUS_5_5 },
  { kind: "model", model: Model.CLAUDE_OPUS_5 },
  { kind: "model", model: Model.CLAUDE_5_5_SONNET },
  { kind: "model", model: Model.CLAUDE_5_SONNET },
  { kind: "model", model: Model.CLAUDE_FABLE_5_1 },
  { kind: "model", model: Model.GPT_6_ASTRA },
  { kind: "model", model: Model.GPT_61_SOL },
  { kind: "model", model: Model.GPT_6_SOL },
  { kind: "model", model: Model.GPT_6_LUNA },
  { kind: "model", model: Model.GPT_56 },
  { kind: "model", model: Model.GPT_56_TERRA },
  { kind: "model", model: Model.GPT_56_LUNA },
  { kind: "model", model: Model.KIMI_K_2_7_THINKING },
  { kind: "model", model: Model.GROK_4_6 },
];

export const DEFAULT_MODEL_FEATURE_CONFIG = {
  features: DEFAULT_MODEL_FEATURE_STATIC_CONFIGS,
  userFacingModels: DEFAULT_USER_FACING_MODELS,
} satisfies ModelFeatureConfig;

/**
 * Per-client entry of the `model-provider-config` flag. Declares the ordered
 * list of providers to try for a given AI client, with optional per-model
 * overrides.
 */
export const zModelProviderClientConfig = z.object({
  // Required fallback provider order for any model without an explicit override.
  defaultProviderOrder: z.array(z.nativeEnum(AiProviderType)),
  // Optional per-model overrides, keyed by model name (e.g. CLAUDE_4_7_SONNET).
  // A string key keeps forward-compat when a newer model name appears before the
  // enum is updated.
  models: z
    .record(
      z.string(),
      z.object({ providerOrder: z.array(z.nativeEnum(AiProviderType)) }),
    )
    .optional(),
});
export type ModelProviderClientConfig = z.infer<
  typeof zModelProviderClientConfig
>;

/**
 * Schema for the `model-provider-config` LaunchDarkly flag. Maps a model to an
 * ordered list of providers (e.g. Anthropic direct -> Bedrock -> Vertex),
 * scoped per AI client. Provider order is required per client; there is no
 * cross-client root default because providers do not overlap across clients.
 *
 * Resolution precedence (see getModelProviderOrder in @hex/server):
 *   clients[clientType].models[model].providerOrder
 *     -> clients[clientType].defaultProviderOrder
 *
 * BYOK always overrides this config.
 */
export const zModelProviderConfig = z.object({
  // Required. Keyed by AiClientType (e.g. ANTHROPIC).
  clients: z.record(z.string(), zModelProviderClientConfig),
});
export type ModelProviderConfig = z.infer<typeof zModelProviderConfig>;

export const DEFAULT_MODEL_PROVIDER_CONFIG: ModelProviderConfig = {
  clients: {
    [AiClientType.ANTHROPIC]: {
      defaultProviderOrder: [
        AiProviderType.ANTHROPIC,
        // TODO: This provider order system is currently in development
        // and we don't actually support any providers other than Anthropic
        // for non-BYOK use cases yet. Eventually this will allow us to fall
        // back to Bedrock and Vertex.
        // AiProviderType.BEDROCK,
        // AiProviderType.VERTEX,
      ],
    },
  },
};

export const OpenAiApiConfig = z.object({
  organization: z.string().optional(),
  apiKey: z.string(),
  baseURL: z.string().optional(),
});
export type OpenAiApiConfig = z.infer<typeof OpenAiApiConfig>;
export const MagicApiConfig = OpenAiApiConfig;
export type MagicApiConfig = z.infer<typeof MagicApiConfig>;

export const MagicTypeAheadProviderConfig = z.object({
  provider: MagicTypeAheadProviderLiteral,
  config: zAnyNonNullish(),
});
export type MagicTypeAheadProviderConfig = z.infer<
  typeof MagicTypeAheadProviderConfig
>;

export const MagicAcceptTypeLiteral = z.enum([
  "KEEP",
  "KEEP_AND_RUN",
  "KEEP_IN_NEW_CELL",
  "KEEP_AND_RUN_IN_NEW_CELL",
  "KEEP_AND_EDIT",
  "REJECT",
]);
export type MagicAcceptType = z.infer<typeof MagicAcceptTypeLiteral>;
export const MagicAcceptType = getNormalEnum(MagicAcceptTypeLiteral);

export const MagicCancelReasonLiteral = z.enum([
  "MANUAL",
  "GEN_CODE_ERROR",
  "TIMEOUT",
  "NO_DATA",
  "UNSUPPORTED",
  "DEFLECTION",
]);
export type MagicCancelReason = z.infer<typeof MagicCancelReasonLiteral>;
export const MagicCancelReason = getNormalEnum(MagicCancelReasonLiteral);

export const AgentFeedbackTypeLiteral = z.enum(["THUMBS", "FLAG"]);
export type AgentFeedbackType = z.infer<typeof AgentFeedbackTypeLiteral>;
export const AgentFeedbackType = getNormalEnum(AgentFeedbackTypeLiteral);

export const FlagForReviewReasonLiteral = z.enum([
  "MISSING_CONTEXT",
  "BAD_DATA_SELECTION",
  "UNEXPECTED_BEHAVIOR",
]);
export type FlagForReviewReason = z.infer<typeof FlagForReviewReasonLiteral>;
export const FlagForReviewReason = getNormalEnum(FlagForReviewReasonLiteral);

export function isFlagForReviewReason(
  reason: string | null | undefined,
): reason is FlagForReviewReason {
  return reason != null && zGuard(FlagForReviewReasonLiteral, reason);
}

// Subset of FlagForReviewReason that we treat as actionable feedback for the review agent
export const ACTIONABLE_FLAG_FOR_REVIEW_AGENT: ReadonlySet<FlagForReviewReason> =
  new Set([
    FlagForReviewReason.MISSING_CONTEXT,
    FlagForReviewReason.BAD_DATA_SELECTION,
  ]);

export function isActionableFlagForReviewReason(
  reason: string | null | undefined,
): reason is FlagForReviewReason {
  return (
    isFlagForReviewReason(reason) &&
    ACTIONABLE_FLAG_FOR_REVIEW_AGENT.has(reason)
  );
}

export const FLAG_REASON_LABELS: Record<FlagForReviewReason, string> = {
  [FlagForReviewReason.MISSING_CONTEXT]: "Missing context or information",
  [FlagForReviewReason.BAD_DATA_SELECTION]: "Bad table/data selection",
  [FlagForReviewReason.UNEXPECTED_BEHAVIOR]: "Unexpected behavior",
};

export function getFlagReasonLabel(reason: string | null): string {
  return isFlagForReviewReason(reason)
    ? FLAG_REASON_LABELS[reason]
    : FLAG_REASON_LABELS[FlagForReviewReason.UNEXPECTED_BEHAVIOR];
}

export const ColumnReference = z.object({
  name: z.string(),
  type: z.string(),
  description: Nullish(z.string()),
  dataManagerDescription: z.string().optional(),
  vectorDistance: z.number().optional(),
  // Populated for columns of semantic-layer-backed tables (e.g. Snowflake
  // semantic views, Databricks metric views). MEASURE / DIMENSION / FACT.
  semanticLayerType: z.enum(["MEASURE", "DIMENSION", "FACT"]).optional(),
  // For semantic views, the underlying logical-table name the column lives on.
  // Used in Snowflake `SEMANTIC_VIEW(<view> METRICS <sourceTable.column>)`.
  sourceTableName: Nullish(z.string()),
});
export type ColumnReference = z.infer<typeof ColumnReference>;

// TODO: Seems bad to just duplicate this definition, but it won't import correctly from hex_sl_schema...
export const MeasureFilter = z.object({
  sql: z.string().or(z.null()).optional(),
  calc: z.union([z.string(), zAnyNonNullish()]).or(z.null()).optional(),
});
export type MeasureFilter = z.infer<typeof MeasureFilter>;

export const SemanticDatasetColumnReference = ColumnReference.extend({
  semanticType: z.literal("MEASURE").or(z.literal("DIMENSION")),
  filters: z.array(MeasureFilter).optional(),
});
export type SemanticDatasetColumnReference = z.infer<
  typeof SemanticDatasetColumnReference
>;

export const MagicDataframeReferenceTypeLiteral = z.enum([
  "DATAFRAME",
  "SNOWPARK_DATAFRAME",
  "REMOTE_DATAFRAME",
  "BIGFRAMES_DATAFRAME",
]);
export const MagicDataframeReferenceType = getNormalEnum(
  MagicDataframeReferenceTypeLiteral,
);

export const MagicTableReferenceTypeLiteral = z.literal("TABLE");
export const MagicSemanticDatasetReferenceTypeLiteral =
  z.literal("SEMANTIC_DATASET");

export const MagicReferenceTypeLiteral = z.enum([
  ...MagicDataframeReferenceTypeLiteral.options,
  MagicSemanticDatasetReferenceTypeLiteral.value,
  MagicTableReferenceTypeLiteral.value,
]);
export const MagicReferenceType = getNormalEnum(MagicReferenceTypeLiteral);
export type MagicReferenceType = z.infer<typeof MagicReferenceTypeLiteral>;

export const ReferenceInfo = z.object({
  dataConnectionId: DataConnectionId.optional(),
  table: z.string(),
  type: MagicReferenceTypeLiteral,
  tableId: DataSourceTableId.optional(),
  tableDescription: Nullish(z.string()),
  schema: z.string().or(z.null()),
  schemaId: DataSourceSchemaId.optional(),
  database: z.string().or(z.null()),
  databaseId: DataSourceDatabaseId.optional(),
  columns: z.array(ColumnReference),
  dataManagerDescription: z.string().optional(),
  tablePrioritized: z.boolean().or(z.null()),
  schemaPrioritized: z.boolean().or(z.null()),
  databasePrioritized: z.boolean().or(z.null()),
  formatted: z.string().optional(),
  categories: z.array(z.string()).optional(),
  status: z.string().or(z.null()).optional(),
  // Optional table-type marker mirroring `DataSourceTable.tableType` for
  // semantic-layer-backed tables (e.g. "SEMANTIC_VIEW" for Snowflake semantic
  // views, "METRIC_VIEW" for Databricks metric views). Null/undefined for
  // regular warehouse tables.
  tableType: Nullish(z.enum(["METRIC_VIEW", "SEMANTIC_VIEW"])),
});

export type ReferenceInfo = z.infer<typeof ReferenceInfo>;

export const SemanticDatasetReferenceInfo = ReferenceInfo.extend({
  database: z.null(),
  schema: z.literal("_hex"),
  type: z.literal("SEMANTIC_DATASET"),
  timeUnits: z.array(z.string()),
  columns: z.array(SemanticDatasetColumnReference),
  joins: z.array(
    z.object({
      name: z.string().or(z.null()),
      target: z.string(),
    }),
  ),
  validQueryPaths: z.array(z.array(z.string())),
});

export type SemanticDatasetReferenceInfo = z.infer<
  typeof SemanticDatasetReferenceInfo
>;
export const MagicClientReferenceInfo = z.union([
  z.object({
    id: DataSourceTableId,
    type: MagicTableReferenceTypeLiteral,
    name: z.string(),
  }),
  z.object({
    name: z.string(),
    type: MagicDataframeReferenceTypeLiteral,
  }),
]);

export type MagicClientReferenceInfo = z.infer<typeof MagicClientReferenceInfo>;

export const CELL_TYPE_BY_PROMPT_TEMPLATE = {
  [PromptTemplateType.GENERATE_SQL]: MagicCellType.SQL,
  [PromptTemplateType.GENERATE_PYTHON]: MagicCellType.CODE,
  [PromptTemplateType.SIMPLE_CHART]: MagicCellType.CHART,
};

// GeneratePromptComponentsOptions is used to generate the project context for a Magic Event
export const GeneratePromptComponentsOptions = z.object({
  traceback: z.string().or(z.null()), // Traceback of the current cell
  contentToEdit: z.string().or(z.null()), // alternative text/code to edit (i.e. not the existing cell source); used for e.g. diff-based-editing autofixes
  agentGeneratedPrompt: z.string().or(z.null()), // A prompt meant for an LLM that was generated by a previous call to the LLM (e.g., the 'Plan' in PlanExec).
  prevErrorDescription: z.string().or(z.null()), // A description of an error the model previously made
});

export type GeneratePromptComponentsOptions = z.infer<
  typeof GeneratePromptComponentsOptions
>;

export const MagicCellContentsReferences = z.object({
  tableReferences: z.array(z.string()),
});
export type MagicCellContentsReferences = z.infer<
  typeof MagicCellContentsReferences
>;

export const MagicLiteralType = z.enum([
  "STRING",
  "FLOAT",
  "INTEGER",
  "BOOLEAN",
  "DATETIME",
]);
export type MagicLiteralType = z.infer<typeof MagicLiteralType>;

export const MagicExploreColumnRef = z.object({
  column: z.string(),
  db: z.string().or(z.null()),
  // schema and table should never be null for new explore generations, but are nullable for
  // backcompat with previously generated suggested prompts
  schema: z.string().or(z.null()),
  table: z.string().or(z.null()),
});
export type MagicExploreColumnRef = z.infer<typeof MagicExploreColumnRef>;

export const MagicExploreStubField = z.object({
  columns: z.array(MagicExploreColumnRef), // Array of column names that this field depends on
  alias: z.string().or(z.null()), // Alias for the field, if any
  identifier: z.string(), // The raw identifier for the field (either a calc expression, column name, literal, or "*")
  type: MagicLiteralType.or(z.null()), // The type of the field, if it's a literal
  agg: HqlAggregationFunctionLiteral.or(z.null()), // The aggregation to apply to the field in the explore spec
  calc: z.boolean(), // Whether the field is a calculated field
});

export type MagicExploreStubField = z.infer<typeof MagicExploreStubField>;

export const UnconsumedQueryElementCategoryLiteral = z.enum([
  "JOIN",
  "FILTER",
  "SELECT",
  "ORDER",
  "ROOT",
  "WINDOW",
]);
export type UnconsumedQueryElementCategory = z.infer<
  typeof UnconsumedQueryElementCategoryLiteral
>;
export const UnconsumedQueryElementCategory = getNormalEnum(
  UnconsumedQueryElementCategoryLiteral,
);

export const MagicExploreRelativeDateField = z.object({
  amount: z.number(),
  unit: RelativeDateUnitLiteral,
  operation: RelativeDateOperationLiteral,
  column: MagicExploreColumnRef,
  calc: z.literal(false),
});
export type MagicExploreRelativeDateField = z.infer<
  typeof MagicExploreRelativeDateField
>;

export const UnconsumedQueryElements = zKeyedRecord(
  UnconsumedQueryElementCategoryLiteral,
  z.array(z.string()),
);
export type UnconsumedQueryElements = z.infer<typeof UnconsumedQueryElements>;

export const MagicExploreStubFilterPredicate = z.union([
  ListBinaryColumnPredicateOpLiteral,
  BinaryColumnPredicateOpLiteral,
  UnaryColumnPredicateOpLiteral,
]);

export const MagicExploreSqlParseOrderBy = z
  .object({
    by: z.array(MagicExploreStubField),
    desc: z.boolean(),
  })
  .or(z.null());

export type MagicExploreSqlParseOrderBy = z.infer<
  typeof MagicExploreSqlParseOrderBy
>;

export const MagicExploreStubFilters = z.array(
  z.object({
    predicate: MagicExploreStubFilterPredicate,
    a: z.union([MagicExploreStubField, MagicExploreRelativeDateField]),
    b: z.union([
      z.array(MagicExploreStubField),
      MagicExploreStubField,
      z.null(),
    ]),
  }),
);
export type MagicExploreStubFilters = z.infer<typeof MagicExploreStubFilters>;

export const MagicExploreStubJoins = z
  .array(
    z.object({
      baseTable: MagicTableReference,
      table: MagicTableReference,
      on: z.object({
        a: MagicExploreStubField,
        b: MagicExploreStubField,
      }),
    }),
  )
  .or(z.null());

export type MagicExploreStubJoins = z.infer<typeof MagicExploreStubJoins>;

export const MagicExploreSqlParse = z.object({
  // All fields selected in the query
  fields: z.array(MagicExploreStubField),
  // Unconsumed query elements, grouped by category If non-empty, indicates the agent response is
  // not expressible in the explore UX
  unconsumedQueryElements: UnconsumedQueryElements,
  // Array of AND-ed filters in the query. Because the explore UX does not support OR filters,
  // those are represented as calculated fields + a simple IS_TRUE filter on the calculated column.
  filters: MagicExploreStubFilters,
  // The table the agent selected from in the query
  table: MagicTableReference,
  // The limit clause in the query --> Not currently used
  limit: z.number().or(z.null()),
  // Specification of the fields to order by in the query
  order: MagicExploreSqlParseOrderBy,
  joins: MagicExploreStubJoins,
});
export type MagicExploreSqlParse = z.infer<typeof MagicExploreSqlParse>;

export const UserMagicConfig = z.object({
  magicEnabled: z.boolean(),
  magicTypeaheadAllowed: z.boolean(),
  onboardedToMagic: z.boolean(),
});
export type UserMagicConfig = z.infer<typeof UserMagicConfig>;

// ProjectContextForPromptGeneration is the context used to generate Prompt Components for a Magic Event
export const ProjectContextForPromptGeneration = z.object({
  previousPrompts: z.array(
    z.object({
      prompt: z.string(),
      referencedTables: z.array(MagicTableReference),
    }),
  ), // Previous prompts in the project
  existingChainCells: z.array(MagicPromptGenerationCellSpec), // Existing cells in the current chain to be generated
  projectCategories: z.array(
    z.object({ name: z.string(), description: z.string().or(z.null()) }),
  ), // Categories of the project
  projectCellSources: z.array(
    z.object({
      contents: z.string(),
      title: z.string(),
    }),
  ), // Contents and title of each cell in the project
  projectTitle: z.string(), // Title of the project
  projectVariables: z.array(VariableName), // Existing in-scope variables for the project that the agent should not overwrite in its generated code
  projectImports: z.array(z.string()), // Existing imports in the project that the agent should not include in its generated code
  previousEdits: z.array(
    z.object({
      prompt: z.string(),
      result: z.string(),
    }),
  ),
  previousExploreEdits: z.array(
    z.object({
      id: MagicEventId,
      prompt: z.string(),
      result: z.string().or(z.null()),
      functionArguments: z.string().or(z.null()),
      type: MagicKeywordLiteral,
    }),
  ),
  projectStatus: z.object({
    name: z.string(),
    description: z.string().or(z.null()),
  }),
  magicRules: z.string().or(z.null()),
  chainContents: z.array(
    z.object({ cellType: MagicCellTypeLiteral, contents: z.string() }),
  ),
  lastCellOutput: DisplayTableOutput,
  targetCellContents: z.string(),
  exploreImage: z.object({
    base64Content: z.string(),
    mimeType: MagicSupportedImageMimeTypes,
  }),
  exploreOutputs: z.object({
    table: DisplayTableOutput.optional(),
    rowCount: z.number().optional(),
    pageSize: z.number().optional(),
  }),
  exploreConfig: z.object({
    sqlQuery: z.string().optional(), // Raw SQL query string the model generated.
    sqlParse: MagicExploreSqlParse.optional(), // Query string parsed to "magic stub" schema.
  }),
  previousError: z
    .object({
      description: z.string(), // A description of the failure that's causing us to retry the event.
      result: z.string(), // The result of the previous event.
    })
    .or(z.null()),
  relatedCellSources: z.array(
    z.object({
      contents: z.string(),
      language: z.string(),
      title: z.string(),
    }),
  ), // Contents and metadata of related cells for context
});

export type ProjectContextForPromptGeneration = z.infer<
  typeof ProjectContextForPromptGeneration
>;

// Magic API Key types

// These are fields we should mask from being returned to a user
export const SecureField = z.enum([
  "apiKey",
  "privateKey",
  "privateKeyPassphrase",
  "serviceAccountJson",
]);
export type SecureField = z.infer<typeof SecureField>;

/**
 * Header names an org admin may configure as BYOK custom headers, covering
 * the common AI gateway auth headers. Names are matched case-insensitively
 * (HTTP header names are case-insensitive).
 *
 * The allowlist is enforced when saving new or changed headers; keys saved
 * before the allowlist existed may contain other header names and continue
 * to work at runtime.
 */
export const BYOK_CUSTOM_HEADER_ALLOWLIST: readonly string[] = [
  "Authorization",
  "x-api-key",
  "api-key",
  // Portkey
  "x-portkey-api-key",
  "x-portkey-virtual-key",
  "x-portkey-config",
  "x-portkey-provider",
];

export function isAllowedByokCustomHeader(name: string): boolean {
  const normalized = name.trim().toLowerCase();
  return BYOK_CUSTOM_HEADER_ALLOWLIST.some(
    (allowed) => allowed.toLowerCase() === normalized,
  );
}

export const OpenAIMagicApiKeyDetails = z.object({
  apiKey: z.string(),
  openAIOrgId: z.string(),
  baseURL: z.string().optional(),
  customHeaders: z.record(z.string(), z.string()).optional(),
});

export type OpenAIMagicApiKeyDetails = z.infer<typeof OpenAIMagicApiKeyDetails>;

const SharedAnthropicLikeMagicApiKeyDetails = z.object({
  baseUrl: z.string().optional(),
  customModelMapping: zKeyedRecord(
    AnthropicModelLiteral,
    z.string().optional(),
  ).optional(),
  customHeaders: z.record(z.string(), z.string()).optional(),
});

export const AnthropicMagicApiKeyDetails =
  SharedAnthropicLikeMagicApiKeyDetails.extend({
    apiKey: z.string(),
  });

export type AnthropicMagicApiKeyDetails = z.infer<
  typeof AnthropicMagicApiKeyDetails
>;

export const BedrockMagicApiKeyDetails =
  SharedAnthropicLikeMagicApiKeyDetails.extend({
    apiKey: z.string(),
    awsRegion: z.string(),
  });
export type BedrockMagicApiKeyDetails = z.infer<
  typeof BedrockMagicApiKeyDetails
>;

const NonEmptyServiceAccountField = z
  .string()
  .refine((value) => value.trim().length > 0, {
    message: "must be a non-empty string",
  });

export const GoogleServiceAccountCredentials = z.object({
  type: z.literal("service_account"),
  client_email: NonEmptyServiceAccountField,
  private_key: NonEmptyServiceAccountField,
  project_id: NonEmptyServiceAccountField,
});
export type GoogleServiceAccountCredentials = z.infer<
  typeof GoogleServiceAccountCredentials
>;

type GoogleServiceAccountJsonValidation =
  | { valid: true; credentials: GoogleServiceAccountCredentials }
  | { valid: false; reason: string };

function validateGoogleServiceAccountJson(
  serviceAccountJson: string,
): GoogleServiceAccountJsonValidation {
  let parsedCredentials: unknown;
  try {
    parsedCredentials = JSON.parse(serviceAccountJson);
  } catch {
    return { valid: false, reason: "Vertex credentials must be valid JSON" };
  }

  const result = GoogleServiceAccountCredentials.safeParse(parsedCredentials);
  if (!result.success) {
    return {
      valid: false,
      reason: "Vertex credentials must be a Google service account JSON object",
    };
  }

  const { client_email, private_key, project_id } = result.data;
  return {
    valid: true,
    credentials: {
      type: "service_account",
      client_email,
      private_key,
      project_id,
    },
  };
}

export function parseGoogleServiceAccountJson(
  serviceAccountJson: string,
): GoogleServiceAccountCredentials {
  const validation = validateGoogleServiceAccountJson(serviceAccountJson);
  if (!validation.valid) {
    throw new Error(validation.reason);
  }
  return validation.credentials;
}

// `superRefine`, not `refine`: the runtypes constraint returned its failure
// reason as a string, which `.refine` reads as `true` -- the check would not
// run at all. `superRefine` carries the reason through as the issue message.
const GoogleServiceAccountJson = z.string().superRefine((value, ctx) => {
  const validation = validateGoogleServiceAccountJson(value);
  if (!validation.valid) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: validation.reason });
  }
});

export const GeminiMagicApiKeyDetails =
  SharedAnthropicLikeMagicApiKeyDetails.extend({
    /** @deprecated, use `multiRegion` instead */
    region: GeminiEAPRegionLiteral.optional(),
    /**
     * Which region supports a given model for GEAP is a bit convoluted
     * so instead we support a 'psuedo' multi region that we map to
     * a specific region that works for a given model.
     */
    multiRegion: GeminiEAPMultiRegionLiteral.optional(),
    projectId: z.string(),
    serviceAccountJson: GoogleServiceAccountJson,
  }).refine(
    (details) => details.region != null || details.multiRegion != null,
    { message: "Must specify either region or multiRegion" },
  );
export type GeminiMagicApiKeyDetails = z.infer<typeof GeminiMagicApiKeyDetails>;

export const MagicApiKeyDetails = z.union([
  z.object({
    providerName: z.literal(AiProviderType.OPENAI),
    details: OpenAIMagicApiKeyDetails,
  }),
  z.object({
    providerName: z.literal(AiProviderType.ANTHROPIC),
    details: AnthropicMagicApiKeyDetails,
  }),
  z.object({
    providerName: z.literal(AiProviderType.BEDROCK),
    details: BedrockMagicApiKeyDetails,
  }),
  z.object({
    providerName: z.literal(AiProviderType.VERTEX),
    details: GeminiMagicApiKeyDetails,
  }),
]);
export type MagicApiKeyDetails = z.infer<typeof MagicApiKeyDetails>;

/**
 * The custom base URL (endpoint override) configured on a BYOK key, or
 * `undefined` when the key uses the provider default. OpenAI stores the value
 * as `baseURL`; the Anthropic-like providers store it as `baseUrl`.
 */
export function getMagicApiKeyBaseUrl(
  keyDetails: MagicApiKeyDetails,
): string | undefined {
  switch (keyDetails.providerName) {
    case AiProviderType.OPENAI:
      return keyDetails.details.baseURL ?? undefined;
    case AiProviderType.ANTHROPIC:
    case AiProviderType.BEDROCK:
    case AiProviderType.VERTEX:
      return keyDetails.details.baseUrl ?? undefined;
    default:
      guardNever(keyDetails, keyDetails);
      return undefined;
  }
}

/**
 * Whether a BYOK key is configured with a custom base URL (endpoint override).
 */
export function magicApiKeyUsesBaseUrl(
  keyDetails: MagicApiKeyDetails,
): boolean {
  const baseUrl = getMagicApiKeyBaseUrl(keyDetails);
  return baseUrl != null && baseUrl.length > 0;
}

/**
 * Whether a BYOK key is configured with any non-empty custom model name
 * mapping. OpenAI BYOK keys do not support custom model mappings.
 */
export function magicApiKeyUsesCustomModelNames(
  keyDetails: MagicApiKeyDetails,
): boolean {
  switch (keyDetails.providerName) {
    case AiProviderType.OPENAI:
      return false;
    case AiProviderType.ANTHROPIC:
    case AiProviderType.BEDROCK:
    case AiProviderType.VERTEX:
      return Object.values(keyDetails.details.customModelMapping ?? {}).some(
        (name) => name != null && name.length > 0,
      );
    default:
      guardNever(keyDetails, keyDetails);
      return false;
  }
}

/**
 * Drops `customModelMapping` entries keyed by models Hex no longer supports
 * (e.g. deprecated since the key was saved) from raw stored key details, so
 * one stale entry doesn't make the whole key fail `MagicApiKeyDetails`.
 *
 * Only Anthropic models need handling: `customModelMapping` on the
 * Anthropic-like key details is the only place stored key details reference
 * models. OpenAI key details store no models. If another stored field is ever
 * keyed by model, it needs the same treatment.
 */
export function omitUnsupportedCustomModelMappings(details: unknown): unknown {
  if (!isRecord(details) || !isRecord(details.customModelMapping)) {
    return details;
  }
  const mappedModels = Object.keys(details.customModelMapping);
  if (mappedModels.every((model) => zGuard(AnthropicModelLiteral, model))) {
    return details;
  }
  return {
    ...details,
    customModelMapping: Object.fromEntries(
      Object.entries(details.customModelMapping).filter(([model]) =>
        zGuard(AnthropicModelLiteral, model),
      ),
    ),
  };
}

// Invalid key specification returned from validation failure
export const InvalidMagicApiKey = z.object({
  providerName: AiProviderTypeLiteral,
  message: z.string(),
});
export type InvalidMagicApiKey = z.infer<typeof InvalidMagicApiKey>;

// The types of "dataframe" sources that an explore cell can have.
// This is used by magic to determine how different types of tables/columns/etc
// should be loaded.
export type ExploreSourceType =
  | "DATA_WAREHOUSE"
  | "DATAFRAME"
  | "SEMANTIC_DATASET";
